import { useState, useCallback, useContext } from "react"
import { AuthContext } from "context/context/AuthContext"
import { request_post } from "services/OSRM"
import useCancelToken from "hooks/UseCancelToken"
import Logger from "classes/Logger"
import useLogin from "hooks/UseLogin"
import { TransactionCodeEnum } from "enums/TransactionCodeEnum"
import { serviceListenerSearch } from "services/ListenerServices"

interface TeamPerformanceMetrics {
  preparedOrders: number | null
  enteredOrders: number | null
  pendingOrders: number | null
  storeCompliancePercentage: number | null
}

export interface PendingOrder {
  id: string
  timeWaiting: string
  timeWaitingBg: string
  timeWaitingColor: string
  orderId: string
  channel: string
  shippingType: string
  modality: string
  currentStatus: string
  dateStatus: string
  detailUrl: string
  custom?: any
}

interface UseTeamPerformanceMetricsReturn {
  metrics: TeamPerformanceMetrics
  pendingOrdersList: PendingOrder[] | null
  pendingOrdersError: boolean
  loading: boolean
  error: string | null
  fetchMetrics: (dateFrom: string, dateTo: string, orderType: string, page?: number) => Promise<void>
  maxPage: number
  downloadCsv: (dateFrom: string, dateTo: string, orderType: string) => Promise<void>
  isDownloadingCsv: boolean
  csvProgress: number
}

export const useTeamPerformanceMetrics = (): UseTeamPerformanceMetricsReturn => {
  const [metrics, setMetrics] = useState<TeamPerformanceMetrics>({
    preparedOrders: null,
    enteredOrders: null,
    pendingOrders: null,
    storeCompliancePercentage: null,
  })
  const [pendingOrdersList, setPendingOrdersList] = useState<PendingOrder[] | null>(null)
  const [pendingOrdersError, setPendingOrdersError] = useState<boolean>(false)
  const [loading, setLoading] = useState<boolean>(false)
  const [error, setError] = useState<string | null>(null)
  const [maxPage, setMaxPage] = useState<number>(0)
  const [isDownloadingCsv, setIsDownloadingCsv] = useState<boolean>(false)
  const [csvProgress, setCsvProgress] = useState<number>(0)

  const { newCancelToken, isCancel } = useCancelToken()
  const { logout } = useLogin()
  const { state: { user } } = useContext(AuthContext)

  const handleUnauthorizedError = useCallback((error: any) => {
    if (error.response?.status === TransactionCodeEnum.unauthorized) {
      Logger.error('Error 401 Unauthorized detectado:', error)
      logout()
      return true
    }
    return false
  }, [logout])

  const fetchMetrics = useCallback(async (dateFrom: string, dateTo: string, orderType: string, page: number = 0) => {
    setLoading(true)
    setError(null)

    let shippingType = orderType === "Todos" ? ["HD", "SP"] : [orderType]

    const enteredOrdersBody = {
      size: 10,
      from: 0,
      filters: {
        shippingType: shippingType,
        "custom.deliveryType": "ED",
        ...(user?.allSources ? { "source.id": undefined } : { "source.id": user?.currentSources }),
        status: {
          $elemMatch: {
            tag: "AWAITING_STORE_CONFIRMATION",
            date: {
              from: dateFrom,
              to: dateTo,
            }
          }
        }
      },
      fields: ["id"],
      excludes: {
        "status.tag": [
          "CANCELED",
          "CANCELED_DECREASE",
          "CANCELED_LOGISTIC",
          "IN_RETURN_CUSTOMER",
          "IN_RETURN_COURIER",
          "REJECTED",
          "RETURNED"
        ]
      }
    }


    const preparedOrdersBody = {
      size: 10,
      from: 0,
      filters: {
        shippingType: shippingType,
        "custom.deliveryType": "ED",
        ...(user?.allSources ? { "source.id": undefined } : { "source.id": user?.currentSources }),
        status: {
          $elemMatch: {
            tag: "AWAITING_DELIVERY",
            date: {
              from: dateFrom,
              to: dateTo,
            }
          }
        }
      },
      fields: ["id"],
      excludes: {
        "status.tag": [
          "CANCELED",
          "CANCELED_DECREASE",
          "CANCELED_LOGISTIC",
          "IN_RETURN_CUSTOMER",
          "IN_RETURN_COURIER",
          "REJECTED",
          "RETURNED"
        ]
      }
    }

    const pendingOrdersBody = {
      serviceType: "UNPREPARED_ORDERS",
      size: 5,
      from: page * 5,
      filters: {
        ...(user?.allSources ? { "source.id": undefined } : { "source.id": user?.currentSources }),
      }
    }


    const complianceOrdersBody = {
      size: 1,
      from: 0,
      filters: {
        shippingType: shippingType,
        "custom.deliveryType": "ED",
        ...(user?.allSources ? { "source.id": undefined } : { "source.id": user?.currentSources }),
        "custom.infoCumplimiento.indicadorCumplimiento": "CUMPLE",
        "custom.infoCumplimiento.datePacked": {
          from: dateFrom,
          to: dateTo,
        },
      },
      fields: ["id", "status", "currentStatus", "custom"],
    }

    const totalOrdersBody = {
      size: 1,
      from: 0,
      filters: {
        shippingType: shippingType,
        "custom.deliveryType": "ED",
        ...(user?.allSources ? { "source.id": undefined } : { "source.id": user?.currentSources }),
        "custom.infoCumplimiento.datePacked": {
          from: dateFrom,
          to: dateTo,
        },
      },
      fields: ["id", "status", "currentStatus", "custom"],
    }

    const formatPendingOrders = (data: any[]): PendingOrder[] => {
      const defaultColor = { bg: "#d4edda", color: "#155724" }
      const complianceColors: Record<string, { bg: string; color: string }> = {
        NO_CUMPLE: { bg: "#f5c6cb", color: "#721c24" },
        SIN_CUMPLIR: { bg: "#ffeeba", color: "#856404" },
        CUMPLE: defaultColor,
      }

      return data.map(item => {
        const { bg: timeBg, color: timeColor } = complianceColors[item?.indicadorCumplimiento as string] || defaultColor

        return {
          id: item.id,
          timeWaiting: item?.tiempoTranscurrido || "N/A",
          timeWaitingBg: timeBg,
          timeWaitingColor: timeColor,
          orderId: `${item.orderId}`,
          channel: item.channel?.toUpperCase() || "N/A",
          shippingType: item.shippingType || "N/A",
          modality: item.custom?.deliveryType || "N/A",
          currentStatus: item.currentStatus?.name || item.currentStatus?.tag || "N/A",
          dateStatus: (() => {
            const statusList = Array.isArray(item.status) ? item.status : []
            const awaitingStatus = statusList.find((s: any) => s.tag === "AWAITING_STORE_CONFIRMATION")
            const dateValue = awaitingStatus?.date ?? null
            return dateValue ? new Date(dateValue).toLocaleString('es-ES', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' }).replace(',', '') : "N/A"
          })(),
          detailUrl: (() => {
            const tag = item.currentStatus?.tag
            if (tag === "AWAITING_STORE_CONFIRMATION") return `/order/confirmation/${item.id}/${item.orderId}`
            if (tag === "PENDING_PACKAGE") return `/order/package/${item.id}/${item.orderId}`
            return ""
          })()
        }
      })
    }

    const handleError = (err: any) => {
      if (isCancel(err)) return
      if (handleUnauthorizedError(err)) return
      
      Logger.error("Error fetching team performance metrics:", err)
      if (err.response?.status === 403) {
        setError(err.response?.data?.message || "Acceso denegado a este servicio.")
      } else {
        setError("Error al obtener algunas métricas de rendimiento.")
      }
    }

    if (page === 0) {
      setMetrics({
        preparedOrders: null,
        enteredOrders: null,
        pendingOrders: null,
        storeCompliancePercentage: null,
      })
      setPendingOrdersList(null)
      setPendingOrdersError(false)

      // 1. Prepared Orders
      const p1 = request_post("search/shipping_groups", preparedOrdersBody, newCancelToken())
        .then(res => setMetrics(prev => ({ ...prev, preparedOrders: res.data?.message?.total || 0 })))
        .catch(handleError)

      // 2. Entered Orders
      const p2 = request_post("search/shipping_groups", enteredOrdersBody, newCancelToken())
        .then(res => setMetrics(prev => ({ ...prev, enteredOrders: res.data?.message?.total || 0 })))
        .catch(handleError)

      // 3. Pending Orders y su paginación
      const p3 = serviceListenerSearch(pendingOrdersBody, newCancelToken())
        .then(res => {
          setMetrics(prev => ({ ...prev, pendingOrders: res.data?.message?.total || 0 }))
          setMaxPage(res.data?.message?.pages || 0)
          setPendingOrdersList(formatPendingOrders(res.data?.message?.records || []))
        })
        .catch((err) => {
          handleError(err)
          setPendingOrdersError(true)
        })

      // 4. Compliance (requiere total y compliance)
      const p4 = Promise.all([
        request_post("search/shipping_groups", totalOrdersBody, newCancelToken()),
        request_post("search/shipping_groups", complianceOrdersBody, newCancelToken())
      ]).then(([totalRes, complianceRes]) => {
        const allOrdersTotal = totalRes.data?.message?.total || 0
        const complianceTotal = complianceRes.data?.message?.total || 0
        let compliance = 0
        if (allOrdersTotal > 0 && complianceTotal >= 0) {
          compliance = (complianceTotal / allOrdersTotal) * 100
        }
        setMetrics(prev => ({ ...prev, storeCompliancePercentage: parseFloat(compliance.toFixed(1)) }))
      }).catch(handleError)

      // Esperar a que TODAS terminen de resolver o rechazar para recién apagar el estado global de carga
      Promise.allSettled([p1, p2, p3, p4]).then(() => {
        setLoading(false)
      })
    } else {
      // Al paginar, solo necesitamos las órdenes pendientes
      setPendingOrdersList(null)
      setPendingOrdersError(false)
      serviceListenerSearch(pendingOrdersBody, newCancelToken())
        .then(res => {
          setPendingOrdersList(formatPendingOrders(res.data?.message?.records || []))
        })
        .catch((err) => {
          handleError(err)
          setPendingOrdersError(true)
        })
        .finally(() => {
          setLoading(false)
        })
    }
  }, [newCancelToken, isCancel, handleUnauthorizedError, user])

  const downloadCsv = useCallback(async (dateFrom: string, dateTo: string, orderType: string) => {
    if (!metrics.pendingOrders || metrics.pendingOrders === 0) return
    setIsDownloadingCsv(true)
    setCsvProgress(0)

    try {
      const totalToDownload = Math.min(metrics.pendingOrders || 0, 10000)
      const chunkSize = totalToDownload > 2000 ? 500 : 250
      const totalBatches = Math.ceil(totalToDownload / chunkSize)

      let allRecords: any[] = []

      for (let i = 0; i < totalBatches; i++) {
        const body = {
          size: chunkSize,
          from: i * chunkSize,
          filters: {
            shippingType: ["HD", "SP"],
            ...(user?.allSources ? { "source.id": undefined } : { "source.id": user?.currentSources }),
            "currentStatus.tag": ["AWAITING_STORE_CONFIRMATION", "PENDING_PACKAGE"]
          },
          sortBy: {
            "status.date": {
              order: "ASC",
              mode: "min",
            },
          }
        }

        const response = await request_post("search/shipping_groups", body, newCancelToken())
        const records = response.data?.message?.records || []
        allRecords = allRecords.concat(records)
        setCsvProgress(Math.round(((i + 1) / totalBatches) * 100))
      }

      // Format records
      const csvRows = allRecords.flatMap(item => {
        const baseRowData = [
          item?.orderId || "N/A",
          item?.id || "N/A",
          item?.custom?.customer?.name || "N/A",
          item?.custom?.customer?.id || "N/A",
          item?.source?.id || "N/A",
          item.channel?.toUpperCase() || "N/A",
          item.shippingType || "N/A",
          item.custom?.deliveryType || "N/A",
        ];

        const dateStatus = item.currentStatus?.date
          ? new Date(item.currentStatus.date).toLocaleString('es-ES', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' }).replace(',', '')
          : "N/A"

        let statusName = item.currentStatus?.tag || "N/A";
        if (statusName === "AWAITING_STORE_CONFIRMATION") statusName = "Bandeja de preparación";
        else if (statusName === "PENDING_PACKAGE") statusName = "Packing e impresión de documentos";

        const trailingRowData = [
          statusName,
          dateStatus
        ];

        const sourceItems = Array.isArray(item.source?.items) && item.source.items.length > 0
          ? item.source.items
          : [null];

        return sourceItems.map((prod: any) => {
          let sku = "N/A";
          let quantity = "N/A";
          let series = "N/A";

          if (prod) {
            sku = prod.sku || "N/A";
            quantity = prod.quantity !== undefined && prod.quantity !== null ? String(prod.quantity) : "N/A";

            if (prod.custom?.series) {
              if (Array.isArray(prod.custom.series)) {
                series = prod.custom.series.join(" , ");
              } else {
                series = String(prod.custom.series);
              }
            }
          }

          return [
            ...baseRowData,
            sku,
            quantity,
            series,
            ...trailingRowData
          ];
        });
      })

      // Generate CSV string
      const headers = ["ID Orden", "ID SG", "Nombre Cliente", "RUT Cliente", "Sucursal", "Canal", "Tipo Despacho", "Modalidad Despacho", "Producto SKU", "Cantidad de producto", "Series pickeadas", "Estado de la orden", "Fecha y Hora de preparación de la orden"]
      const csvContent = [
        headers.join(","),
        ...csvRows.map(row => row.map((val: any) => `"${String(val).replace(/"/g, '""')}"`).join(","))
      ].join("\n")

      // Trigger download
      const blob = new Blob(["\uFEFF" + csvContent], { type: 'text/csv;charset=utf-8;' }) // \uFEFF BOM for Excel UTF-8 support
      const url = URL.createObjectURL(blob)
      const link = document.createElement("a")
      link.href = url

      const now = new Date()
      const day = String(now.getDate()).padStart(2, '0')
      const month = String(now.getMonth() + 1).padStart(2, '0')
      const year = now.getFullYear()
      const hours = String(now.getHours()).padStart(2, '0')
      const minutes = String(now.getMinutes()).padStart(2, '0')
      const fileName = `ordenes_pendientes_${day}_${month}_${year}_T_${hours}_${minutes}.csv`

      link.setAttribute("download", fileName)
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
      URL.revokeObjectURL(url)

    } catch (err: any) {
      Logger.error("Error downloading CSV:", err)
    } finally {
      setIsDownloadingCsv(false)
      setTimeout(() => setCsvProgress(0), 500)
    }
  }, [metrics.pendingOrders, newCancelToken, user])

  return { metrics, pendingOrdersList, loading, error, fetchMetrics,    maxPage,
    downloadCsv,
    isDownloadingCsv,
    csvProgress,
    pendingOrdersError
  }
}
